/**
 * Role Color Mapping Utility
 *
 * Provides Tailwind CSS color class mapping for different user roles in the system.
 * Supports both exact matching and fuzzy matching for role names.
 */

// Role color class mapping based on common healthcare roles
const ROLE_COLOR_CLASS_MAP: Record<string, string> = {
  // Medical roles
  doctor: "text-blue-500", // Blue - Professional, authoritative
  physician: "text-blue-500", // Blue - Same as doctor
  consultant: "text-blue-500", // Blue - Senior medical professional
  specialist: "text-blue-500", // Blue - Medical specialist

  // Nursing roles
  nurse: "text-green-500", // Green - Care, health, nurturing
  nursing: "text-green-500", // Green - Nursing related
  matron: "text-green-500", // Green - Senior nurse

  // Pharmacy roles
  pharmacist: "text-purple-500", // Purple - Precision, pharmaceutical
  pharmacy: "text-purple-500", // Purple - Pharmacy related
  dispenser: "text-purple-500", // Purple - Medication dispensing

  // Administrative roles
  admin: "text-orange-500", // Orange - Management, control
  administrator: "text-orange-500", // Orange - Administrative
  manager: "text-orange-500", // Orange - Management
  supervisor: "text-orange-500", // Orange - Supervisory
  coordinator: "text-orange-500", // Orange - Coordination

  // Technical roles
  technician: "text-cyan-500", // Cyan - Technical, support
  tech: "text-cyan-500", // Cyan - Technical
  it: "text-cyan-500", // Cyan - IT support
  support: "text-cyan-500", // Cyan - Support roles

  // Reception/Front desk
  receptionist: "text-pink-500", // Pink - Customer facing
  reception: "text-pink-500", // Pink - Reception
  clerk: "text-pink-500", // Pink - Clerical work
  assistant: "text-pink-500", // Pink - Assistant roles

  // Security/Safety
  security: "text-gray-600", // Dark gray - Security, protection
  guard: "text-gray-600", // Dark gray - Security guard
  safety: "text-gray-600", // Dark gray - Safety officer
};

// Default color class for unknown roles
const DEFAULT_ROLE_COLOR_CLASS = "text-gray-400"; // Light gray

/**
 * Get Tailwind CSS color class for a given role label
 *
 * @param roleLabel - The role label/name to get color class for
 * @returns Tailwind CSS color class string
 *
 * @example
 * ```typescript
 * getRoleColorClass('Doctor') // Returns 'text-blue-500'
 * getRoleColorClass('Senior Nurse') // Returns 'text-green-500'
 * getRoleColorClass('Unknown Role') // Returns 'text-gray-400'
 * ```
 */
export const getRoleColorClass = (roleLabel?: string | null): string => {
  if (!roleLabel || typeof roleLabel !== "string") {
    return DEFAULT_ROLE_COLOR_CLASS;
  }

  // Normalize the role label for matching
  const normalizedRole = roleLabel.toLowerCase().trim();

  // First, try exact match
  if (ROLE_COLOR_CLASS_MAP[normalizedRole]) {
    return ROLE_COLOR_CLASS_MAP[normalizedRole];
  }

  // Then, try fuzzy matching - check if any key is contained in the role label
  for (const [key, colorClass] of Object.entries(ROLE_COLOR_CLASS_MAP)) {
    if (normalizedRole.includes(key)) {
      return colorClass;
    }
  }

  // If no match found, return default color class
  return DEFAULT_ROLE_COLOR_CLASS;
};

/**
 * Get all available role color classes
 *
 * @returns Object mapping role types to Tailwind CSS classes
 */
export const getAllRoleColorClasses = (): Record<string, string> => {
  return { ...ROLE_COLOR_CLASS_MAP };
};

/**
 * Check if a role has a specific color class mapping
 *
 * @param roleLabel - The role label to check
 * @returns Boolean indicating if role has specific color class
 */
export const hasSpecificRoleColorClass = (roleLabel?: string | null): boolean => {
  return getRoleColorClass(roleLabel) !== DEFAULT_ROLE_COLOR_CLASS;
};

// Legacy exports for backward compatibility (deprecated)
/**
 * @deprecated Use getRoleColorClass instead
 */
export const getRoleColor = getRoleColorClass;

/**
 * @deprecated Use getAllRoleColorClasses instead
 */
export const getAllRoleColors = getAllRoleColorClasses;

/**
 * @deprecated Use hasSpecificRoleColorClass instead
 */
export const hasSpecificRoleColor = hasSpecificRoleColorClass;
